import { useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import { saveDistributor, updateDistributor, type Distributor } from '@/services/distributors';

interface DistributorModalProps {
  distributor?: Distributor | null;
  onClose: () => void;
  onSaved: () => void;
}

interface DistributorForm {
  businessName: string;
  contactName: string;
  documentId: string;
  phone: string;
  email: string;
  city: string;
  address: string;
  discountRate: string;
  creditStatus: string;
}

const emptyForm: DistributorForm = {
  businessName: '',
  contactName: '',
  documentId: '',
  phone: '',
  email: '',
  city: '',
  address: '',
  discountRate: '',
  creditStatus: 'Al día',
};

export default function DistributorModal({ distributor, onClose, onSaved }: DistributorModalProps) {
  const isEditing = !!distributor;
  const [form, setForm] = useState<DistributorForm>(
    distributor
      ? {
          businessName: distributor.businessName,
          contactName: distributor.contactName,
          documentId: distributor.documentId,
          phone: distributor.phone,
          email: distributor.email,
          city: distributor.city,
          address: distributor.address,
          discountRate: distributor.discountRate ? String(distributor.discountRate) : '',
          creditStatus: distributor.creditStatus || 'Al día',
        }
      : emptyForm,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set<K extends keyof DistributorForm>(key: K, value: DistributorForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!form.businessName.trim()) {
      setError('El nombre del negocio es obligatorio.');
      return;
    }

    const payload = {
      businessName: form.businessName,
      contactName: form.contactName,
      documentId: form.documentId,
      phone: form.phone,
      email: form.email,
      city: form.city,
      address: form.address,
      discountRate: Number(form.discountRate) || 0,
      creditStatus: form.creditStatus,
    };

    setSaving(true);
    const { error: saveError } =
      isEditing && distributor
        ? await updateDistributor(distributor.id, payload)
        : await saveDistributor(payload);
    setSaving(false);

    if (saveError) {
      setError(saveError);
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <Modal
      title={isEditing ? 'Editar Distribuidor' : 'Nuevo Distribuidor'}
      subtitle={
        isEditing
          ? `Editando: ${distributor?.businessName}`
          : 'Registra un distribuidor con su descuento comercial.'
      }
      onClose={onClose}
      maxWidth="max-w-xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <div>
          <label className={labelCls}>Nombre del negocio *</label>
          <input className={inputCls} value={form.businessName} onChange={(e) => set('businessName', e.target.value)} placeholder="Mundo Mascotas Medellín" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Contacto</label>
            <input className={inputCls} value={form.contactName} onChange={(e) => set('contactName', e.target.value)} placeholder="Andrés Gómez" />
          </div>
          <div>
            <label className={labelCls}>Documento (NIT/C.C.)</label>
            <input className={inputCls} value={form.documentId} onChange={(e) => set('documentId', e.target.value)} placeholder="901234567-8" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Teléfono</label>
            <input className={inputCls} value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+57 3xx xxx xxxx" />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input type="email" className={inputCls} value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="contacto@negocio.com" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Ciudad</label>
            <input className={inputCls} value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="Medellín" />
          </div>
          <div>
            <label className={labelCls}>Dirección</label>
            <input className={inputCls} value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="Cra 45 # 12-34" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Descuento comercial (%)</label>
            <input type="number" min="0" max="100" step="0.5" className={inputCls} value={form.discountRate} onChange={(e) => set('discountRate', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Estado de crédito</label>
            <input className={inputCls} value={form.creditStatus} onChange={(e) => set('creditStatus', e.target.value)} placeholder="Al día" />
          </div>
        </div>

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
            {saving ? (<><i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...</>) : (<><i className="ri-save-line mr-1.5"></i>{isEditing ? 'Guardar cambios' : 'Crear distribuidor'}</>)}
          </button>
        </div>
      </form>
    </Modal>
  );
}